/* eslint-disable @typescript-eslint/no-explicit-any */
// claims-provider-utils.ts
import { RpcError, StatusCode } from "grpc-web";

// Utils
import { convertBffClaimToLocalClaim } from "../helpers";

// Types
import { Claim, LegalBenefit } from "../types";

/**
 * Finds and returns a LegalBenefit by its id.
 */
export const getLegalBenefitFn = (benefits: LegalBenefit[], id: number): LegalBenefit | undefined => {
	return benefits.find((b) => b.id === id);
};

/**
 * Returns the service label based on the provided claim service and benefits list.
 */
export const getServiceLabelFn = (benefits: LegalBenefit[], claimService: any): string => {
	if (!claimService?.serviceId) return "";
	const benefit = getLegalBenefitFn(benefits, claimService.legalBenefitId);
	return benefit ? `${benefit.benefitTitle} ${benefit.matterBillingCode}` : "";
};

/**
 * Extracts the inner error message from nested gRPC error responses.
 * For chunk upload errors, extracts the actual server error from the wrapper.
 */
export const getCleanErrorMessage = (error: RpcError | Error): string => {
	if (!error?.message) return "An unknown error occurred";

	const message = error.message;

	// For chunk upload errors, extract the inner error message
	// Pattern: "Error uploading chunk: status: Internal, message: \"actual error message\""
	const match = message.match(/Error uploading chunk:.*?message: \\"([^"]+)\\"/);
	if (match && match[1]) {
		return match[1];
	}

	return message;
};

/**
 * Handles errors in a unified way.
 * The options parameter allows overriding messages (for example, PERMISSION_DENIED) if needed.
 */
export const handleError = (
	toast: any,
	error: RpcError | undefined,
	customError: string,
	consoleError: string,
	options?: { permissionDeniedMessage?: string }
) => {
	switch (error?.code) {
		case StatusCode.UNAVAILABLE:
			toast("Error", "Server is not responding, please try again later", "error");
			console.error(consoleError, error);
			break;
		case StatusCode.UNAUTHENTICATED:
			toast("Error", "Authentication failed, please log in again", "error");
			console.error(consoleError, error);
			break;
		case StatusCode.INTERNAL:
			// For internal errors, extract the clean message from nested error responses
			const cleanMessage = getCleanErrorMessage(error);
			toast("Error", cleanMessage, "error");
			console.error(consoleError, error);
			break;
		case StatusCode.UNIMPLEMENTED:
			toast("Error", "This functionality has not been implemented, please try again later", "error");
			console.error(consoleError, error);
			break;
		case StatusCode.PERMISSION_DENIED:
			toast("Error", options?.permissionDeniedMessage || "You are not authorized", "error");
			console.error(consoleError, error);
			break;
		case StatusCode.OUT_OF_RANGE:
			toast("Error", error?.message || customError, "error");
			console.error(consoleError, error);
			break;
		default:
			toast("Error", customError, "error");
			console.error(consoleError, error);
	}
};

/**
 * Shared logic to update the benefits from a claim.
 *
 * @param claim The claim object that may contain services.
 * @param currentBenefits The current benefits array.
 * @param fetchBenefit A function that takes a legalBenefitId and returns a promise resolving to a LegalBenefit.
 * @returns A promise resolving to an updated benefits array.
 */
export const updateBenefitsUtil = async (
	claim: Claim,
	currentBenefits: LegalBenefit[],
	fetchBenefit: (legalBenefitId: number, attyId?: number, caseId?: number) => Promise<LegalBenefit | undefined>
): Promise<LegalBenefit[]> => {
	if (claim.claimId && claim.claimServices.length > 0) {
		const workingBenefits = [...currentBenefits];
		const newBenefits: LegalBenefit[] = [];
		for (const svc of claim.claimServices) {
			const exists = workingBenefits.find((b) => b.id === svc.legalBenefitId);
			if (!exists) {
				try {
					const fetched = svc.legalBenefitId
						? await fetchBenefit(svc.legalBenefitId, claim.atty?.getId(), claim.caseId)
						: undefined;
					if (fetched) newBenefits.push(fetched);
				} catch (err) {
					// You might want to rethrow or handle errors here.
					console.error(`Error fetching benefit ${svc.legalBenefitId}`, err);
				}
			}
		}
		return workingBenefits.concat(newBenefits);
	}
	return currentBenefits;
};

/**
 * Shared logic for fetching a case.
 *
 * @param caseId The id of the case.
 * @param fetchCaseFn A function to fetch the case (usually getCase).
 * @returns A promise resolving to the case (or undefined if not found).
 */
export const handleFetchCaseUtil = async (
	caseId: number,
	fetchCaseFn: (caseId: number) => Promise<any>
): Promise<any> => {
	try {
		const response = await fetchCaseFn(caseId);
		const foundCase = response.getCase();
		if (foundCase) {
			return foundCase;
		}
	} catch (error) {
		console.error("Error fetching case", error);
		throw error;
	}
	return undefined;
};

/**
 * Shared logic for upserting a claim.
 *
 * @param updatedClaim The claim object to upsert.
 * @param upsertFn A function to upsert the claim (usually upsertClaim).
 * @returns A promise resolving to the converted claim.
 */
export const handleClaimUpsertUtil = async (
	updatedClaim: Claim,
	upsertFn: (claim: Claim) => Promise<any>
): Promise<Claim | undefined> => {
	try {
		const response = await upsertFn(updatedClaim);
		const responseClaim = response.getClaim();
		if (responseClaim) {
			return convertBffClaimToLocalClaim(responseClaim);
		}
	} catch (error) {
		console.error("Error upserting claim", error);
		throw error;
	}
	return undefined;
};
